// Typed client for the Skill Studio REST API (/v1/studio/skills/*).
// See app/api/studio_skills_router.py.

import { apiBlob, apiRequest, apiUpload } from "./http";
import type {
  KnowledgeChunk,
  KnowledgeSearchResult,
  Skill,
  SkillDocument,
  SkillWithStats,
} from "../types/skill";

const enc = encodeURIComponent;

export const listSkills = () => apiRequest<SkillWithStats[]>("/studio/skills");

export const getSkill = (id: string) => apiRequest<Skill>(`/studio/skills/${enc(id)}`);

export const createSkill = (skill: Skill) =>
  apiRequest<Skill>("/studio/skills", { method: "POST", body: skill });

export const updateSkill = (skill: Skill) =>
  apiRequest<Skill>(`/studio/skills/${enc(skill.id)}`, { method: "PUT", body: skill });

export const archiveSkill = (id: string) =>
  apiRequest<Skill>(`/studio/skills/${enc(id)}`, { method: "DELETE" });

export const publishSkill = (id: string) =>
  apiRequest<Skill>(`/studio/skills/${enc(id)}/publish`, { method: "POST" });

export const unpublishSkill = (id: string) =>
  apiRequest<Skill>(`/studio/skills/${enc(id)}/unpublish`, { method: "POST" });

export const setSkillEnabled = (id: string, enabled: boolean) =>
  apiRequest<Skill>(`/studio/skills/${enc(id)}/enabled`, {
    method: "POST",
    body: { enabled },
  });

export const listDocuments = (skillId: string) =>
  apiRequest<SkillDocument[]>(`/studio/skills/${enc(skillId)}/documents`);

export const uploadDocument = (skillId: string, file: File) => {
  const form = new FormData();
  form.append("file", file, file.name);
  return apiUpload<SkillDocument>(`/studio/skills/${enc(skillId)}/documents`, form);
};

export const deleteDocument = (skillId: string, documentId: string) =>
  apiRequest<{ deleted: string }>(
    `/studio/skills/${enc(skillId)}/documents/${enc(documentId)}`,
    { method: "DELETE" },
  );

export const addUrlSource = (skillId: string, url: string) =>
  apiRequest<SkillDocument>(`/studio/skills/${enc(skillId)}/documents/url`, {
    method: "POST",
    body: { url },
  });

export const getDocumentChunks = (skillId: string, documentId: string) =>
  apiRequest<KnowledgeChunk[]>(
    `/studio/skills/${enc(skillId)}/documents/${enc(documentId)}/chunks`,
  );

export const downloadDocument = (skillId: string, documentId: string) =>
  apiBlob(`/studio/skills/${enc(skillId)}/documents/${enc(documentId)}/download`);

export const reingestDocument = (skillId: string, documentId: string) =>
  apiRequest<SkillDocument>(
    `/studio/skills/${enc(skillId)}/documents/${enc(documentId)}/reingest`,
    { method: "POST" },
  );

export const searchTest = (skillId: string, query: string, topK = 5) =>
  apiRequest<KnowledgeSearchResult[]>(`/studio/skills/${enc(skillId)}/search-test`, {
    method: "POST",
    body: { query, topK },
  });

export const getGuideSkills = (guideId: string) =>
  apiRequest<{ guideId: string; skillIds: string[] }>(`/studio/guides/${enc(guideId)}/skills`);

export const setGuideSkills = (guideId: string, skillIds: string[]) =>
  apiRequest<{ guideId: string; skillIds: string[] }>(`/studio/guides/${enc(guideId)}/skills`, {
    method: "PUT",
    body: { skillIds },
  });
